import React, { useState, useMemo, useEffect } from 'react';
import {
  Appointment,
  User,
  Service,
  Venue,
  AddOn,
  SystemConfig,
  Customer,
  Company,
  UserGroup,
  Resource,
  Permission,
  PricingTier,
  QuestionnaireTemplate,
  QuestionnaireSubmission
} from '@/types';
import {
  Calendar,
  Activity,
  Users,
  Key,
  Settings,
  Building2,
  Home,
  Shield,
  MapPin,
  Lock,
  FileText,
  Monitor,
  PoundSterling,
  Loader2
} from 'lucide-react';

import { ReportsTab } from './tabs/ReportsTab';
import { BookingsTab } from './tabs/BookingsTab';
import { StaffTab } from './tabs/StaffTab';
import { UserGroupsTab } from './tabs/UserGroupsTab';
import { CustomersTab } from './tabs/CustomersTab';
import { CompaniesTab } from './tabs/CompaniesTab';
import { ServicesTab } from './tabs/ServicesTab';
import { VenuesTab } from './tabs/VenuesTab';
import { SettingsTab } from './tabs/SettingsTab';
import { QuestionnairesTab } from './tabs/QuestionnairesTab';
import { SystemStatusTab } from './tabs/SystemStatusTab';
import { FinanceTab } from './tabs/FinanceTab';
import { api } from '@/services/api';

interface AdminDashboardProps {
  isRefreshing?: boolean;
  currentUser: User;
  currentSessionId?: string | null;
  services: Service[];
  venues: Venue[];
  addOns: AddOn[];
  userGroups: UserGroup[];
  customers?: Customer[];
  companies?: Company[];
  systemConfig?: SystemConfig;
  questionnaires: QuestionnaireTemplate[];
  submissions: QuestionnaireSubmission[];
  onUpdateService: (service: Service) => void;
  onUpdateVenue: (venue: Venue) => void;
  onUpdateAddOn: (addOn: AddOn) => void;
  onAddService: (service: Service) => void;
  onAddVenue: (venue: Venue) => void;
  onAddAddOn: (addOn: AddOn) => void;
  onAddUser: (user: User) => void;
  onUpdateUser: (user: User) => void;
  onDeleteUser: (userId: string) => Promise<void>;
  onUpdateCustomer?: (customer: Customer) => void;
  onDeleteService: (id: string) => void;
  onDeleteVenue: (id: string) => void;
  onDeleteAddOn: (id: string) => void;
  onResetPassword: (userId: string, newPassword?: string) => void;
  onUpdateSystemConfig?: (config: SystemConfig) => void;
  onAddCompany?: (company: Company) => void;
  onApproveCompany?: (
    companyId: string,
    tier: PricingTier,
    paymentPref: 'invoice' | 'card',
    requirePo: boolean
  ) => void;
  onRejectCompany?: (companyId: string) => void;
  onUpdateCompany?: (company: Company) => void;
  onDeleteCompany?: (companyId: string) => Promise<void> | void;
  onAddUserGroup: (group: UserGroup) => void;
  onCurrentSessionRevoked?: () => void | Promise<void>;
  onUpdateUserGroup: (group: UserGroup) => void;
  onDeleteUserGroup: (groupId: string) => void;
  onSendReminder: (
    appointmentId: string,
    type: 'sms' | 'email' | 'form',
    templateType?: string
  ) => Promise<void> | void;
  onUpdateBooking: (booking: Appointment, notify: { email: boolean; sms: boolean }) => void;
  onAddBooking: (booking: Appointment) => void;
  onDeleteBooking: (id: string) => void;
  onForceRefresh?: () => void;
  onAddQuestionnaire: (q: QuestionnaireTemplate) => void;
  onUpdateQuestionnaire: (q: QuestionnaireTemplate) => void;
  onDeleteQuestionnaire: (id: string) => void;
}

type Tab = 'reports' | 'bookings' | 'finance' | 'admin' | 'customers' | 'companies';
type AdminSubTab =
  | 'dashboard'
  | 'users'
  | 'groups'
  | 'services'
  | 'venues'
  | 'questionnaires'
  | 'settings';

export const AdminDashboard: React.FC<AdminDashboardProps> = (props) => {
  const { currentUser, userGroups = [] } = props;

  const { currentUserGroup, myPermissions } = useMemo(() => {
    const group = userGroups.find((g) => g.id === currentUser.userGroupId);
    const isExplicitAdmin = group?.type === 'admin' || group?.type === 'global_admin';
    const isGlobalAdmin = group?.type === 'global_admin';

    const defaultPerms: Record<Resource, Permission> = {
      reports: { view: isExplicitAdmin, edit: isExplicitAdmin },
      bookings: { view: true, edit: true },
      customers: { view: true, edit: isExplicitAdmin },
      companies: { view: true, edit: isExplicitAdmin },
      staff: { view: isExplicitAdmin, edit: isExplicitAdmin },
      user_groups: { view: isExplicitAdmin, edit: isExplicitAdmin },
      services: { view: isExplicitAdmin, edit: isExplicitAdmin },
      venues: { view: isExplicitAdmin, edit: isExplicitAdmin },
      settings: { view: isExplicitAdmin, edit: isExplicitAdmin },
      questionnaires: { view: true, edit: isExplicitAdmin },
      finance: { view: isExplicitAdmin, edit: isExplicitAdmin }
    };

    const mergePermissions = (
      base: Record<Resource, Permission>,
      override?: Partial<Record<Resource, Partial<Permission>>>
    ) => {
      const merged = { ...base };
      (Object.keys(base) as Resource[]).forEach((key) => {
        merged[key] = { ...base[key], ...(override?.[key] || {}) };
      });
      return merged;
    };

    const mergedPermissions = isExplicitAdmin
      ? defaultPerms
      : mergePermissions(defaultPerms, group?.permissions);

    return { currentUserGroup: group, myPermissions: mergedPermissions };
  }, [currentUser, userGroups]);

  const getInitialTab = (): Tab => {
    if (myPermissions.reports.view) return 'reports';
    if (myPermissions.bookings.view) return 'bookings';
    if (myPermissions.finance.view) return 'finance';
    return 'bookings';
  };

  const [activeTab, setActiveTab] = useState<Tab>(getInitialTab());
  const [adminSubTab, setAdminSubTab] = useState<AdminSubTab>('dashboard');
  const [targetCustomerId, setTargetCustomerId] = useState<string | null>(null);

  const canEdit = (resource: Resource) => Boolean(myPermissions[resource]?.edit);
  const canViewVenues = Boolean(myPermissions.venues?.view);
  const canEditVenues = canEdit('venues');
  const [pendingCompanyCount, setPendingCompanyCount] = useState(0);

  const showAdminTab =
    myPermissions.staff.view ||
    myPermissions.user_groups.view ||
    myPermissions.services.view ||
    myPermissions.venues.view ||
    myPermissions.settings.view ||
    myPermissions.questionnaires.view;

  const handleAdminTabClick = () => {
    setActiveTab('admin');
    setAdminSubTab('dashboard');
  };

  const handleViewCustomer = (customerId: string) => {
    setTargetCustomerId(customerId);
    setActiveTab('customers');
  };

  useEffect(() => {
    let isActive = true;
    if (!myPermissions.companies.view) return () => undefined;
    api
      .listStaffCompanies({ page: 1, pageSize: 1, status: 'pending' })
      .then((data) => {
        if (!isActive) return;
        setPendingCompanyCount(Number(data.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setPendingCompanyCount(0);
      });

    return () => {
      isActive = false;
    };
  }, [myPermissions.companies.view]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500">
            Welcome back, {currentUser.name}
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
              {currentUserGroup?.name || 'Authorized User'}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-1 bg-white rounded-lg p-1 border shadow-sm">
          {myPermissions.reports.view && (
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'reports' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Home className="w-4 h-4" /> Reports
            </button>
          )}
          {myPermissions.finance.view && (
            <button
              onClick={() => setActiveTab('finance')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'finance' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <PoundSterling className="w-4 h-4" /> Finance
            </button>
          )}
          {myPermissions.bookings.view && (
            <button
              onClick={() => setActiveTab('bookings')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'bookings' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Calendar className="w-4 h-4" /> Bookings
            </button>
          )}
          {myPermissions.customers.view && (
            <button
              onClick={() => setActiveTab('customers')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'customers' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Users className="w-4 h-4" /> Customers
            </button>
          )}
          {myPermissions.companies.view && (
            <button
              onClick={() => setActiveTab('companies')}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'companies' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'} relative`}
            >
              <Building2 className="w-4 h-4" /> Companies
              {pendingCompanyCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center animate-pulse">
                  {pendingCompanyCount}
                </span>
              )}
            </button>
          )}
          {showAdminTab && (
            <button
              onClick={handleAdminTabClick}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'admin' ? 'bg-teal-50 text-teal-700' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Lock className="w-4 h-4" /> Admin & Config
            </button>
          )}
        </div>
      </div>

      <div className="min-h-[500px] relative">
        {props.isRefreshing && (
          <div className="absolute inset-0 z-20 bg-white/60 backdrop-blur-[1px] rounded-xl flex items-start justify-center pt-10 pointer-events-none">
            <div className="bg-white border border-slate-200 shadow-sm rounded-full px-4 py-2 flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-teal-600" />
              Syncing latest updates
            </div>
          </div>
        )}
        {activeTab === 'reports' && myPermissions.reports.view && (
          <ReportsTab venues={props.venues} services={props.services} />
        )}

        {activeTab === 'finance' && myPermissions.finance.view && (
          <FinanceTab services={props.services} addOns={props.addOns} venues={props.venues} />
        )}

        {activeTab === 'bookings' && myPermissions.bookings.view && (
          <BookingsTab
            services={props.services}
            venues={props.venues}
            addOns={props.addOns}
            customers={props.customers || []}
            companies={props.companies || []}
            submissions={props.submissions}
            onSendReminder={props.onSendReminder}
            onUpdateBooking={props.onUpdateBooking}
            onAddBooking={props.onAddBooking}
            onDeleteBooking={props.onDeleteBooking}
            onViewCustomer={handleViewCustomer}
            onForceRefresh={props.onForceRefresh}
          />
        )}

        {activeTab === 'admin' && showAdminTab && (
          <div className="animate-fade-in">
            <div className="flex gap-2 mb-6 border-b overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setAdminSubTab('dashboard')}
                className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'dashboard' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Monitor className="w-4 h-4" /> Dashboard
              </button>
              {myPermissions.services.view && (
                <button
                  onClick={() => setAdminSubTab('services')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'services' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <Activity className="w-4 h-4" /> Services
                </button>
              )}
              {myPermissions.questionnaires.view && (
                <button
                  onClick={() => setAdminSubTab('questionnaires')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'questionnaires' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <FileText className="w-4 h-4" /> Forms
                </button>
              )}
              {canViewVenues && (
                <button
                  onClick={() => setAdminSubTab('venues')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'venues' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <MapPin className="w-4 h-4" /> Locations
                </button>
              )}
              {myPermissions.staff.view && (
                <button
                  onClick={() => setAdminSubTab('users')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'users' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <Key className="w-4 h-4" /> Staff
                </button>
              )}
              {myPermissions.user_groups.view && (
                <button
                  onClick={() => setAdminSubTab('groups')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'groups' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <Shield className="w-4 h-4" /> Groups
                </button>
              )}
              {myPermissions.settings.view && (
                <button
                  onClick={() => setAdminSubTab('settings')}
                  className={`pb-2 px-4 font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all ${adminSubTab === 'settings' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <Settings className="w-4 h-4" /> Settings
                </button>
              )}
            </div>

            <div className="animate-fade-in-up">
              {adminSubTab === 'dashboard' && (
                <SystemStatusTab
                  currentSessionId={props.currentSessionId}
                  onCurrentSessionRevoked={props.onCurrentSessionRevoked}
                />
              )}
              {adminSubTab === 'users' && myPermissions.staff.view && (
                <StaffTab
                  userGroups={props.userGroups}
                  venues={props.venues}
                  currentUserGroup={currentUserGroup!}
                  onAddUser={props.onAddUser}
                  onUpdateUser={props.onUpdateUser}
                  onDeleteUser={props.onDeleteUser}
                  canEdit={canEdit('staff')}
                />
              )}
              {adminSubTab === 'groups' && myPermissions.user_groups.view && (
                <UserGroupsTab
                  onAddUserGroup={props.onAddUserGroup}
                  onUpdateUserGroup={props.onUpdateUserGroup}
                  onDeleteUserGroup={props.onDeleteUserGroup}
                  canEdit={canEdit('user_groups')}
                />
              )}
              {adminSubTab === 'services' && myPermissions.services.view && (
                <ServicesTab
                  onAddService={props.onAddService}
                  onUpdateService={props.onUpdateService}
                  onDeleteService={props.onDeleteService}
                  onAddAddOn={props.onAddAddOn}
                  onUpdateAddOn={props.onUpdateAddOn}
                  onDeleteAddOn={props.onDeleteAddOn}
                  canEdit={canEdit('services')}
                />
              )}
              {adminSubTab === 'questionnaires' && myPermissions.questionnaires.view && (
                <QuestionnairesTab
                  services={props.services}
                  addOns={props.addOns}
                  onAddQuestionnaire={props.onAddQuestionnaire}
                  onUpdateQuestionnaire={props.onUpdateQuestionnaire}
                  onDeleteQuestionnaire={props.onDeleteQuestionnaire}
                  canEdit={canEdit('questionnaires')}
                />
              )}
              {adminSubTab === 'venues' && canViewVenues && (
                <VenuesTab
                  onAddVenue={props.onAddVenue}
                  onUpdateVenue={props.onUpdateVenue}
                  onDeleteVenue={props.onDeleteVenue}
                  canEdit={canEditVenues}
                />
              )}
              {adminSubTab === 'settings' && myPermissions.settings.view && (
                <SettingsTab
                  systemConfig={props.systemConfig}
                  onUpdateSystemConfig={props.onUpdateSystemConfig}
                  canEdit={canEdit('settings')}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === 'customers' && myPermissions.customers.view && (
          <CustomersTab
            customers={props.customers || []}
            services={props.services}
            questionnaires={props.questionnaires || []}
            onUpdateCustomer={props.onUpdateCustomer}
            onResetPassword={props.onResetPassword}
            onSendReminder={props.onSendReminder}
            canEdit={canEdit('customers')}
            targetCustomerId={targetCustomerId}
            onClearTargetCustomer={() => setTargetCustomerId(null)}
            systemConfig={props.systemConfig}
          />
        )}

        {activeTab === 'companies' && myPermissions.companies.view && (
          <CompaniesTab
            venues={props.venues}
            addOns={props.addOns}
            onAddCompany={props.onAddCompany}
            onApproveCompany={props.onApproveCompany}
            onRejectCompany={props.onRejectCompany}
            onUpdateCompany={props.onUpdateCompany}
            onDeleteCompany={props.onDeleteCompany}
            canEdit={canEdit('companies')}
            onPendingCountChange={setPendingCompanyCount}
          />
        )}
      </div>
    </div>
  );
};
